import { useEffect, useState, Fragment } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Minus } from "lucide-react";
import { toast } from "sonner";

// label + whether edit is configurable for clients
const MODULES = [
  { key: "overview", label: "Overview", editable: false },
  { key: "budget", label: "Budget", editable: false },
  { key: "approvals", label: "Approvals", editable: true },
  { key: "ffe", label: "FF&E", editable: false },
  { key: "checklists", label: "Checklists / Assignments", editable: false },
  { key: "documents", label: "Documents", editable: false },
  { key: "moodboards", label: "Mood Boards", editable: true },
  { key: "scheme", label: "Design Scheme", editable: true },
  { key: "communication", label: "Notes", editable: true },
  { key: "calendar", label: "Calendar", editable: false },
  { key: "photos", label: "Photos", editable: true },
  { key: "assistant", label: "AI Assistant", editable: true },
];

export default function ClientAccessDialog({ projectId, open, onOpenChange, onSaved }) {
  const [perms, setPerms] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && projectId) {
      api.get(`/projects/${projectId}/client-permissions`)
        .then((r) => setPerms(r.data.client_permissions))
        .catch(() => setPerms(null));
    }
  }, [open, projectId]);

  const setView = (key, value) =>
    setPerms((p) => ({ ...p, [key]: { ...p[key], view: value, edit: value ? (p[key]?.edit || false) : false } }));
  const setEdit = (key, value) =>
    setPerms((p) => ({ ...p, [key]: { ...p[key], view: value ? true : (p[key]?.view || false), edit: value } }));
  const setBudgetHideTrade = (value) =>
    setPerms((p) => ({ ...p, budget: { ...p.budget, hide_trade_markup: value } }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/client-permissions`, { client_permissions: perms });
      toast.success("Client access updated for this project");
      onSaved?.(data.client_permissions);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save access");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="client-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2B2622]">Client Access</DialogTitle>
          <DialogDescription className="text-sm text-[#5C5651]">
            Choose what this project's client can view and edit. Applies to this project only.
          </DialogDescription>
        </DialogHeader>

        {!perms ? (
          <p className="text-sm text-[#9b938b] py-8 text-center">Loading…</p>
        ) : (
          <div className="border border-[#EBE5E0]">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F8F6]">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#A68A6B]">
                  <th className="p-3 font-medium">Section</th>
                  <th className="p-3 font-medium text-center w-24">Client view</th>
                  <th className="p-3 font-medium text-center w-24">Client edit</th>
                </tr>
              </thead>
              <tbody>
                {MODULES.map((m) => {
                  const p = perms[m.key] || { view: false, edit: false };
                  const locked = m.key === "overview";
                  return (
                    <Fragment key={m.key}>
                    <tr className="border-t border-[#F2EFEB]" data-testid={`access-row-${m.key}`}>
                      <td className="p-3 text-[#2B2622]">{m.label}</td>
                      <td className="p-3 text-center">
                        {locked ? (
                          <span className="text-[10px] uppercase tracking-wider text-[#9b938b]">Always</span>
                        ) : (
                          <Switch data-testid={`access-view-${m.key}`} checked={!!p.view}
                            onCheckedChange={(v) => setView(m.key, v)}
                            className="data-[state=checked]:bg-[#A68A6B]" />
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {m.editable ? (
                          <Switch data-testid={`access-edit-${m.key}`} checked={!!p.edit}
                            onCheckedChange={(v) => setEdit(m.key, v)}
                            className="data-[state=checked]:bg-[#A68A6B]" />
                        ) : (
                          <Minus size={14} strokeWidth={2} className="text-[#C9BBAE] mx-auto" />
                        )}
                      </td>
                    </tr>
                    {m.key === "budget" && p.view && (
                      <tr className="border-t border-[#F2EFEB] bg-[#FBFAF8]" data-testid="access-subrow-budget-trade">
                        <td className="py-2.5 pl-8 pr-3 text-[#5C5651] text-xs">
                          ↳ Trade/Net Cost &amp; Markup Visible
                        </td>
                        <td className="p-3 text-center">
                          <Switch data-testid="access-trade-visible" checked={!p.hide_trade_markup}
                            onCheckedChange={(v) => setBudgetHideTrade(!v)}
                            className="data-[state=checked]:bg-[#A68A6B]" />
                        </td>
                        <td></td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Button data-testid="client-access-save-btn" onClick={save} disabled={busy || !perms}
          className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
          {busy ? "Saving…" : "Save Access"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
